import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Sparkles, Zap, MessageCircle, Search,
  Phone, Mail, Thermometer, Clock, Calendar, AlertTriangle, Send,
  Megaphone, Trash2, Instagram, Linkedin, Youtube, MapPin, ChevronDown,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Sidebar from "../components/Sidebar";
import AppHeader from "../components/AppHeader";
import ErrorState from "../components/ErrorState";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import ClaireHelpButton from "../components/ClaireHelpButton";
import LookalikeModal from "../components/LookalikeModal";
import ScoreBreakdownModal from "../components/ScoreBreakdownModal";
import WhatsAppModal from "../components/WhatsAppModal";
import LeadDetailDrawer from "../components/LeadDetailDrawer";
import SequenceModal from "../components/SequenceModal";
import OutreachConfigModal from "../components/OutreachConfigModal";
import KanbanTools from "../components/KanbanTools";
import WABulkModal from "../components/WABulkModal";
import BottomSheet, { useIsMobile } from "../components/BottomSheet";
import Logo from "../components/Logo";
import { useLang } from "../i18n/LangContext";
import { useMetaAdsBadges } from "../hooks/useMetaAdsBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMN_COLORS = {
  NEW:        { bg: "#EFF6FF", text: "#3B82F6", border: "#BFDBFE" },
  CONTACTED:  { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  QUALIFIED:  { bg: "#FFEDD5", text: "#C2410C", border: "#FED7AA" },
  PROPOSAL:   { bg: "#E9D5FF", text: "#7C3AED", border: "#DDD6FE" },
  WON:        { bg: "#DCFCE7", text: "#16A34A", border: "#BBF7D0" },
  LOST:       { bg: "#FEE2E2", text: "#DC2626", border: "#FECACA" },
};

const TEMP_COLOR = {
  QUENTE: { bg: "#FEE2E2", text: "#DC2626" },
  MORNO:  { bg: "#FFEDD5", text: "#C2410C" },
  FRIO:   { bg: "#DCFCE7", text: "#16A34A" },
};

const STALE_DAYS = 7; // a lead "parado" longer than this gets a warning indicator

const TEMPS = ["QUENTE", "MORNO", "FRIO"];

function daysSince(iso) {
  if (!iso) return 0;
  try {
    const t0 = new Date(iso).getTime();
    if (Number.isNaN(t0)) return 0;
    return Math.floor((Date.now() - t0) / (1000 * 60 * 60 * 24));
  } catch (_e) { return 0; }
}

function fmtShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
  } catch (_e) { return ""; }
}

export default function Kanban() {
  const isMobile = useIsMobile();
  const { t, lang } = useLang();
  const isPt = lang === "pt";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moving, setMoving] = useState(false);
  const [lookalikeOpen, setLookalikeOpen] = useState(false);
  const [lookalikeSeeds, setLookalikeSeeds] = useState([]);
  const [scoreLead, setScoreLead] = useState(null);
  const [waLead, setWaLead] = useState(null);
  const [drawerLead, setDrawerLead] = useState(null);
  const [sequenceLead, setSequenceLead] = useState(null);
  const [outreachConfigOpen, setOutreachConfigOpen] = useState(false);
  // Filters — `temp` can be pre-populated via URL (?temp=QUENTE) from Insights drill-downs.
  const [search, setSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("q") || "";
  });
  const [tempFilter, setTempFilter] = useState(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search).get("temp") || "";
    return ["QUENTE", "MORNO", "FRIO"].includes(p) ? p : "";
  });
  // Multi-sector filter: Set<string>. Empty = all.
  const [sectorFilter, setSectorFilter] = useState(() => new Set());
  const [sectorMenuOpen, setSectorMenuOpen] = useState(false);
  const toggleSector = (name) => setSectorFilter((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });
  const clearSectors = () => setSectorFilter(new Set());
  const [silentOnly, setSilentOnly] = useState(false);
  const [addrFilter, setAddrFilter] = useState("");
  const [waBulkOpen, setWaBulkOpen] = useState(false);
  const [addrJob, setAddrJob] = useState(null);
  const [emailJob, setEmailJob] = useState(null);
  const [discJob, setDiscJob] = useState(null);
  const [autoVerify, setAutoVerify] = useState(false);

  useEffect(() => {
    axios.get(`${API}/email/settings`)
      .then(({ data }) => setAutoVerify(!!data.auto_verify)).catch(() => {});
  }, []);

  const toggleAutoVerify = async () => {
    const next = !autoVerify;
    setAutoVerify(next);
    try {
      await axios.post(`${API}/email/settings`, { auto_verify: next });
      toast.success(next
        ? (isPt ? "Auto-Verify ativado: emails de novos leads serão verificados" : "Auto-Verify on")
        : (isPt ? "Auto-Verify desativado" : "Auto-Verify off"));
    } catch (_e) { setAutoVerify(!next); }
  };

  const startEmailBulk = async () => {
    try {
      const { data: job } = await axios.post(`${API}/email/leads/verify-bulk`, {});
      if (!job.total) { toast.info(isPt ? "Todos os emails já verificados" : "All emails verified"); return; }
      setEmailJob({ ...job, done: 0 });
      toast.success(isPt ? `Verificando ${job.total} emails...` : `Verifying ${job.total} emails...`);
      const poll = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/email/leads/verify-jobs/${job.job_id}`);
          setEmailJob(st);
          if (st.status === "done") {
            clearInterval(poll);
            const c = st.counts || {};
            toast.success(isPt
              ? `Emails: ✅ ${c.valid || 0} válidos · ⚠️ ${c.risky || 0} genéricos · ❌ ${c.invalid || 0} inválidos`
              : `Emails — valid: ${c.valid || 0} · risky: ${c.risky || 0} · invalid: ${c.invalid || 0}`);
            load();
            setTimeout(() => setEmailJob(null), 8000);
          }
        } catch (_e) { clearInterval(poll); }
      }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "email bulk failed");
    }
  };

  const startDiscoverBulk = async () => {
    try {
      const { data: job } = await axios.post(`${API}/leads/discover-emails-bulk`, { limit: 150 });
      if (!job.total) { toast.info(isPt ? "Nenhum lead sem e-mail (com site)" : "No leads missing email (with website)"); return; }
      setDiscJob({ ...job, done: 0, found: 0 });
      toast.success(isPt ? `Buscando e-mails de ${job.total} leads (site → Hunter → Apollo)...` : `Discovering emails for ${job.total} leads...`);
      const poll = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/leads/discover-email-jobs/${job.job_id}`);
          setDiscJob(st);
          if (st.status === "done") {
            clearInterval(poll);
            toast.success(isPt
              ? `E-mails: 🎯 ${st.found || 0} encontrados · ${st.not_found || 0} sem resultado`
              : `Emails — found: ${st.found || 0} · not found: ${st.not_found || 0}`);
            load();
            setTimeout(() => setDiscJob(null), 8000);
          }
        } catch (_e) { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "email discovery failed");
    }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      // Iter108 — pass through ?source=sunbiz so the board shows only Sunbiz leads.
      const sp = new URLSearchParams(window.location.search);
      const src = (sp.get("source") || "").trim();
      const url = src ? `${API}/kanban?source=${encodeURIComponent(src)}` : `${API}/kanban`;
      const { data: d } = await axios.get(url);
      setData(d);
      // Keep drawer in sync if its lead changed
      if (drawerLead) {
        const fresh = (d.columns || [])
          .flatMap((c) => c.leads || [])
          .find((ld) => ld.id === drawerLead.id);
        if (fresh) setDrawerLead(fresh);
      }
    } catch (e) {
      setError(e?.message || "Failed to load Kanban");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Iter156 — sector catalog fetched from the same backend endpoint used
  // by the Leads Map so numbers match everywhere. Falls back to values
  // present in the current data payload.
  const [sectorCatalog, setSectorCatalog] = useState([]); // [{sector, count}]
  useEffect(() => {
    axios.get(`${API}/leads/sectors`)
      .then(({ data }) => setSectorCatalog(data.sectors || []))
      .catch(() => { /* silent — dropdown falls back to derived list */ });
  }, []);

  const sectors = useMemo(() => {
    if (sectorCatalog.length) return sectorCatalog.map((r) => r.sector);
    const set = new Set();
    (data?.columns || []).forEach((c) => (c.leads || []).forEach((ld) => {
      if (ld.sector) set.add(ld.sector);
    }));
    return Array.from(set).sort();
  }, [data, sectorCatalog]);
  const sectorCountByName = useMemo(() => {
    const m = new Map();
    sectorCatalog.forEach((r) => m.set(r.sector, r.count));
    return m;
  }, [sectorCatalog]);

  // Apply filters per column (preserve original ordering)
  const visibleColumns = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.columns.map((col) => {
      const filtered = (col.leads || []).filter((ld) => {
        if (q && !((ld.name || "").toLowerCase().includes(q))) return false;
        if (tempFilter && (ld.badge || "MORNO") !== tempFilter) return false;
        if (sectorFilter.size > 0 && !sectorFilter.has(ld.sector || "")) return false;
        if (silentOnly) {
          // Silent = in CONTACTED/QUALIFIED/PROPOSAL with NO email logged
          const st = (ld.crm_status || "NEW").toUpperCase();
          if (st === "NEW" || st === "WON" || st === "LOST") return false;
          if (ld.last_email_at) return false;
        }
        if (addrFilter) {
          const st = ld.address_status || "unverified";
          if (addrFilter === "missing_zip" && ld.zip_code) return false;
          if (addrFilter === "missing_city" && ld.city) return false;
          if (addrFilter === "missing_state" && ld.state) return false;
          if (addrFilter === "unverified" && ["verified", "manually_confirmed"].includes(st)) return false;
          if (addrFilter === "needs_review" && !["needs_review", "multiple_matches", "ai_suggested"].includes(st)) return false;
          if (addrFilter === "verified" && !["verified", "manually_confirmed"].includes(st)) return false;
        }
        return true;
      });
      return { ...col, _filtered: filtered, _filteredCount: filtered.length };
    });
  }, [data, search, tempFilter, sectorFilter, silentOnly, addrFilter]);

  // Meta Ad Library per-lead badges (cache-only on mount; on-demand refresh).
  // Iter46: the hook also auto-fires competitor-ads cold email when badge is green.
  const allLeads = useMemo(() => {
    if (!data) return [];
    return data.columns.flatMap((c) => (c.leads || []).filter((l) => l.name));
  }, [data]);
  const { badges: adBadges, refresh: refreshAdBadge } = useMetaAdsBadges(allLeads);

  const totalAfterFilter = visibleColumns.reduce((s, c) => s + c._filteredCount, 0);

  const filteredLeads = useMemo(
    () => visibleColumns.flatMap((c) => c._filtered || []),
    [visibleColumns],
  );

  const startAddrBulk = async () => {
    const ids = filteredLeads.map((l) => l.id).filter(Boolean).slice(0, 200);
    if (!ids.length) { toast.error(isPt ? "Nenhum lead visível" : "No visible leads"); return; }
    try {
      const { data: job } = await axios.post(`${API}/leads/address/bulk-complete`, {
        lead_ids: ids, only_missing: true,
      });
      setAddrJob({ job_id: job.job_id, total: job.total, completed: 0 });
      toast.success(isPt
        ? `Analisando endereços de ${job.total} leads em segundo plano...`
        : `Analyzing addresses for ${job.total} leads in background...`);
      const poll = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/leads/address/bulk-jobs/${job.job_id}`);
          setAddrJob(st);
          if (st.status === "done") {
            clearInterval(poll);
            toast.success(isPt
              ? `Endereços: ${st.completed} completados · ${st.needs_review} p/ revisão · ${st.no_match} sem resultado`
              : `Addresses — Completed: ${st.completed} · Needs review: ${st.needs_review} · No match: ${st.no_match}`);
            load();
            setTimeout(() => setAddrJob(null), 8000);
          }
        } catch (_e) { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "bulk address failed");
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const fromStatus = result.source.droppableId;
    const toStatus = result.destination.droppableId;
    if (fromStatus === toStatus && result.source.index === result.destination.index) return;
    const leadId = result.draggableId;
    if (fromStatus === toStatus) return; // no reorder persistence inside the same column

    // Optimistic update
    const prevData = data;
    setData((d) => {
      if (!d) return d;
      const cols = d.columns.map((c) => ({ ...c, leads: [...c.leads] }));
      const fromCol = cols.find((c) => c.status === fromStatus);
      const toCol = cols.find((c) => c.status === toStatus);
      if (!fromCol || !toCol) return d;
      const idx = fromCol.leads.findIndex((ld) => ld.id === leadId);
      if (idx === -1) return d;
      const [moved] = fromCol.leads.splice(idx, 1);
      moved.crm_status = toStatus;
      toCol.leads.unshift(moved);
      fromCol.count = fromCol.leads.length;
      toCol.count = toCol.leads.length;
      return { ...d, columns: cols };
    });
    setMoving(true);
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: toStatus });
      toast.success(`${fromStatus} → ${toStatus}`);
    } catch (_e) {
      toast.error("Failed to move lead");
      setData(prevData);
    } finally {
      setMoving(false);
    }
  };

  const openLookalikeFromTop = () => {
    const seeds = (data?.columns || [])
      .flatMap((c) => c.leads || [])
      .filter((ld) => ld.badge === "QUENTE" || ld.crm_status === "QUALIFIED")
      .slice(0, 6)
      .map((ld) => ld.id)
      .filter(Boolean);
    setLookalikeSeeds(seeds);
    setLookalikeOpen(true);
  };

  const openLookalikeFromLead = (ld) => {
    if (!ld?.id) return;
    setLookalikeSeeds([ld.id]);
    setLookalikeOpen(true);
  };

  const deleteLead = async (ld) => {
    if (!ld?.id) return;
    const confirmed = window.confirm(t("kanban.delete_confirm", { name: ld.name || ld.id }));
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/leads/${ld.id}`);
      toast.success(t("kanban.delete_ok", { name: ld.name || ld.id }));
      await load();
    } catch (e) {
      toast.error(t("kanban.delete_fail"));
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState testid="kanban-error" title="Kanban unavailable" message={error} onRetry={load} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="kanban-header"
          title={t("kanban.title")}
          subtitle={`${totalAfterFilter} / ${data.total} ${t("kanban.subtitle")}`}
          logo={<Logo className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0" />}
          mobileVisible={
            <button
              data-testid="kanban-refresh-btn-mobile"
              onClick={load}
              title={t("dash.refresh")}
              className="w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] inline-flex items-center justify-center"
            >
              <RefreshCw className={`w-4 h-4 ${moving ? "animate-spin" : ""}`} />
            </button>
          }
          mobileMenu={<>
            <button type="button" onClick={openLookalikeFromTop}
              data-testid="kanban-lookalike-btn-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5 text-[#A855F7]">
              <Zap className="w-4 h-4" /> {t("look.button")}
            </button>
            <button type="button" onClick={() => setOutreachConfigOpen(true)}
              data-testid="kanban-outreach-config-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5">
              <Send className="w-4 h-4" /> {t("outreach.config_button") || "Email Config"}
            </button>
            <button type="button" onClick={startDiscoverBulk}
              disabled={!!discJob && discJob.status !== "done"}
              data-testid="kanban-email-discover-bulk-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5 disabled:opacity-60">
              <Mail className="w-4 h-4" /> {isPt ? "Achar E-mails" : "Find Emails"}
            </button>
            <button type="button" onClick={startEmailBulk}
              disabled={!!emailJob && emailJob.status !== "done"}
              data-testid="kanban-email-bulk-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5 disabled:opacity-60">
              <Mail className="w-4 h-4" /> {isPt ? "Verificar Emails" : "Verify Emails"}
            </button>
            <button type="button" onClick={() => setWaBulkOpen(true)}
              data-testid="kanban-wa-bulk-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5 text-[#25D366]">
              <MessageCircle className="w-4 h-4" /> {isPt ? "WhatsApp em lote" : "Bulk WhatsApp"}
            </button>
            <div className="flex items-center gap-1 px-2 py-1.5 border-t border-[var(--al-border)] mt-1">
              <ClaireHelpButton testid="kanban-mobile"
                question="Como uso o CRM Kanban — mover leads entre estágios, criar cadências e ler o histórico?" />
              <ThemeToggle />
              <LangToggle />
            </div>
          </>}
        >
          <ThemeToggle />
          <LangToggle />
          <KanbanTools isPt={isPt} onChanged={load} />
          <ClaireHelpButton
            testid="kanban"
            question="Como uso o CRM Kanban — mover leads entre estágios, criar cadências e ler o histórico?"
          />
            <button
              data-testid="kanban-lookalike-btn"
              onClick={openLookalikeFromTop}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#A855F7] bg-[#FAF5FF] text-[12.5px] font-semibold text-[#A855F7] hover:bg-[#F3E8FF] transition-colors"
            >
              <Zap className="w-3.5 h-3.5" /> {t("look.button")}
            </button>
            <button
              data-testid="kanban-refresh-btn"
              onClick={load}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${moving ? "animate-spin" : ""}`} /> {t("dash.refresh")}
            </button>
            <button
              type="button"
              data-testid="kanban-outreach-config"
              onClick={() => setOutreachConfigOpen(true)}
              title={t("outreach.config_button") || "Email Config"}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-emerald-500 hover:text-emerald-500 transition-colors"
            >
              <Send className="w-3.5 h-3.5" /> {t("outreach.config_button") || "Email Config"}
            </button>
            <button
              type="button"
              data-testid="kanban-email-discover-bulk"
              onClick={startDiscoverBulk}
              disabled={!!discJob && discJob.status !== "done"}
              title={isPt ? "Descobrir e-mails dos leads sem e-mail (varre o site do lead → Hunter → Apollo)" : "Discover emails for leads missing one (website scrape → Hunter → Apollo)"}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors disabled:opacity-60"
            >
              {discJob && discJob.status !== "done"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Mail className="w-3.5 h-3.5" />}
              {discJob && discJob.status !== "done"
                ? `${discJob.done || 0}/${discJob.total} (🎯${discJob.found || 0})`
                : (isPt ? "Achar E-mails" : "Find Emails")}
            </button>
            <button
              type="button"
              data-testid="kanban-email-bulk"
              onClick={startEmailBulk}
              disabled={!!emailJob && emailJob.status !== "done"}
              title={isPt ? "Verificar emails (sintaxe + descartáveis + MX) — lista à prova de bounce" : "Verify emails (syntax + disposable + MX)"}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-emerald-500 hover:text-emerald-500 transition-colors disabled:opacity-60"
            >
              {emailJob && emailJob.status !== "done"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Mail className="w-3.5 h-3.5" />}
              {emailJob && emailJob.status !== "done"
                ? `${emailJob.done || 0}/${emailJob.total}`
                : (isPt ? "Verificar Emails" : "Verify Emails")}
            </button>
            <button
              type="button"
              data-testid="kanban-autoverify-toggle"
              onClick={toggleAutoVerify}
              title={isPt ? "Quando ligado, emails de novos leads são verificados automaticamente" : "Auto-verify new leads' emails"}
              className={`flex items-center gap-1.5 px-3 h-9 rounded-lg border text-[12.5px] font-semibold transition-colors ${
                autoVerify ? "border-emerald-500 text-emerald-500 bg-emerald-500/10"
                  : "border-[var(--al-border)] text-[var(--al-text-muted)] bg-[var(--al-card-bg)]"}`}
            >
              <span className={`w-2 h-2 rounded-full ${autoVerify ? "bg-emerald-500" : "bg-gray-400"}`} />
              Auto-Verify
            </button>
            <button
              type="button"
              data-testid="kanban-addr-bulk"
              onClick={startAddrBulk}
              disabled={!!addrJob && addrJob.status !== "done"}
              title={isPt ? "Completar endereços dos leads visíveis com IA" : "Complete addresses for visible leads"}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#EA4335] hover:text-[#EA4335] transition-colors disabled:opacity-60"
            >
              {addrJob && addrJob.status !== "done"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <MapPin className="w-3.5 h-3.5" />}
              {addrJob && addrJob.status !== "done"
                ? `${addrJob.completed + (addrJob.needs_review || 0) + (addrJob.no_match || 0) + (addrJob.skipped || 0)}/${addrJob.total}`
                : (isPt ? "Completar Endereços" : "Complete Addresses")}
            </button>
            <button
              type="button"
              data-testid="kanban-wa-bulk"
              onClick={() => setWaBulkOpen(true)}
              title={isPt ? "Preparar mensagens de WhatsApp em lote" : "Prepare WhatsApp Messages"}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#25D366] text-[#25D366] bg-[var(--al-card-bg)] text-[12.5px] font-semibold hover:bg-[#25D36610] transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {isPt ? "WhatsApp em lote" : "Bulk WhatsApp"}
            </button>
            <button
              type="button"
              data-testid="kanban-bulk-enrich"
              onClick={async () => {
                const lng = (window.localStorage.getItem("alp.lang") === "pt") ? "pt" : "en";
                try {
                  const { data } = await axios.post(`${API}/leads/enrich-bulk?limit=100&lang=${lng}`);
                  toast.success(
                    lng === "pt"
                      ? `Enriquecimento iniciado para ${data.queued} lead(s). Aparece em ~1min.`
                      : `Enrichment queued for ${data.queued} lead(s). Visible in ~1min.`
                  );
                  setTimeout(load, 8000);
                } catch (_e) {
                  toast.error("Bulk enrich failed");
                }
              }}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[12.5px] font-bold transition-colors"
              title="Enrich missing decision-makers + social URLs (AI)"
            >
              <Sparkles className="w-3.5 h-3.5" />
              AI Enrich
            </button>
        </AppHeader>

        {/* Filter bar */}
        <div className="px-6 pt-4">
          <div className="al-card px-3 py-2.5 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-3.5 h-3.5 text-[var(--al-text-muted)] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                data-testid="kanban-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("kanban.search_placeholder")}
                className="al-input h-8 pl-8 text-[12.5px] w-full"
              />
            </div>
            <select
              data-testid="kanban-filter-temp"
              value={tempFilter}
              onChange={(e) => setTempFilter(e.target.value)}
              className="al-input h-8 text-[12.5px] min-w-[160px]"
            >
              <option value="">{t("kanban.filter_temp_all")}</option>
              {TEMPS.map((tp) => (<option key={tp} value={tp}>{tp}</option>))}
            </select>
            {/* Iter156 — multi-sector checkbox popover (mirrors Leads Map).
                Iter334 · On mobile, opens as a portalled bottom sheet
                so the panel never bleeds behind adjacent filters. */}
            <div className="relative">
              <button
                type="button"
                data-testid="kanban-filter-sector"
                onClick={() => setSectorMenuOpen((v) => !v)}
                className="al-input h-8 text-[12.5px] min-w-[180px] flex items-center justify-between gap-2"
              >
                <span className="truncate">
                  {sectorFilter.size === 0
                    ? t("kanban.filter_sector_all")
                    : `${sectorFilter.size} ${isPt ? "setores" : "sectors"}`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
              </button>
              {sectorMenuOpen && !isMobile && (
                <div className="absolute z-40 mt-1 left-0 w-64 max-h-72 overflow-y-auto rounded-lg border border-[var(--al-border)] shadow-2xl p-1.5"
                     style={{ backgroundColor: "var(--al-card-bg, #FFFFFF)" }}
                     data-testid="kanban-sector-menu">
                  <SectorMenuList
                    sectors={sectors} sectorFilter={sectorFilter}
                    toggleSector={toggleSector}
                    sectorCountByName={sectorCountByName}
                    clearSectors={clearSectors} isPt={isPt} />
                </div>
              )}
              <BottomSheet
                open={sectorMenuOpen && isMobile}
                onClose={() => setSectorMenuOpen(false)}
                title={isPt ? "Setores" : "Sectors"}
                subtitle={sectorFilter.size === 0
                  ? (isPt ? "Todos" : "All")
                  : `${sectorFilter.size}/${sectors.length}`}
                doneLabel={isPt ? "Concluir" : "Done"}
                testid="kanban-sector-sheet"
              >
                <div className="p-2">
                  <SectorMenuList
                    sectors={sectors} sectorFilter={sectorFilter}
                    toggleSector={toggleSector}
                    sectorCountByName={sectorCountByName}
                    clearSectors={clearSectors} isPt={isPt} mobile />
                </div>
              </BottomSheet>
            </div>
            <select
              data-testid="kanban-filter-address"
              value={addrFilter}
              onChange={(e) => setAddrFilter(e.target.value)}
              className="al-input h-8 text-[12.5px] min-w-[170px]"
            >
              <option value="">{isPt ? "Endereço: todos" : "Address: all"}</option>
              <option value="missing_zip">{isPt ? "Sem ZIP Code" : "Missing ZIP Code"}</option>
              <option value="missing_city">{isPt ? "Sem cidade" : "Missing City"}</option>
              <option value="missing_state">{isPt ? "Sem estado" : "Missing State"}</option>
              <option value="unverified">{isPt ? "Não verificado" : "Unverified Address"}</option>
              <option value="needs_review">{isPt ? "Precisa de revisão" : "Address Needs Review"}</option>
              <option value="verified">{isPt ? "Verificado" : "Verified Address"}</option>
            </select>
            <button
              type="button"
              data-testid="kanban-filter-silent"
              onClick={() => setSilentOnly((s) => !s)}
              title={t("kanban.filter_silent_tooltip")}
              className={`h-8 px-3 text-[11.5px] font-semibold rounded-md inline-flex items-center gap-1.5 border transition-colors ${silentOnly ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#DC2626]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#DC2626] hover:text-[#DC2626]"}`}
            >
              <AlertTriangle className="w-3 h-3" />
              {t("kanban.filter_silent")}
            </button>
            {(search || tempFilter || sectorFilter.size > 0 || silentOnly) && (
              <button
                type="button"
                data-testid="kanban-filter-clear"
                onClick={() => { setSearch(""); setTempFilter(""); clearSectors(); setSilentOnly(false); }}
                className="h-8 px-2.5 text-[11.5px] text-[var(--al-text-muted)] hover:text-[#DC2626]"
              >
                ×
              </button>
            )}
            <span className="text-[10.5px] text-[var(--al-text-muted)] ml-auto italic hidden md:inline">
              {t("kanban.drag_hint")}
            </span>
          </div>
        </div>

        <main className="px-6 py-4 max-w-[1700px] mx-auto">
          {totalAfterFilter === 0 && (search || tempFilter || sectorFilter.size > 0 || silentOnly || addrFilter) ? (
            <div data-testid="kanban-no-results" className="al-card p-8 text-center text-[12.5px] text-[var(--al-text-muted)]">
              {t("kanban.no_results")}
            </div>
          ) : null}

          <DragDropContext onDragEnd={onDragEnd}>
            <div className="overflow-x-auto pb-6" data-testid="kanban-board">
              <div className="grid grid-flow-col auto-cols-[300px] gap-4">
                {visibleColumns.map((col) => {
                  const c = COLUMN_COLORS[col.status] || COLUMN_COLORS.NEW;
                  return (
                    <section
                      key={col.status}
                      data-testid={`kanban-col-${col.status}`}
                      className="al-card p-0 overflow-hidden"
                    >
                      <header
                        className="px-3 py-2.5 border-b flex items-center justify-between"
                        style={{ background: c.bg, borderColor: c.border }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[12.5px] font-bold" style={{ color: c.text }}>
                            {t(`kanban.col.${col.status}`)}
                          </span>
                          <span
                            className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ background: "#fff", color: c.text }}
                          >
                            {col._filteredCount}
                            {col._filteredCount !== col.count ? `/${col.count}` : ""}
                          </span>
                        </div>
                      </header>
                      <Droppable droppableId={col.status}>
                        {(dropProvided, dropSnapshot) => (
                          <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            data-testid={`kanban-drop-${col.status}`}
                            className={`p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto transition-colors ${dropSnapshot.isDraggingOver ? "bg-[#EFF6FF]/50" : ""}`}
                          >
                            {col._filtered.length === 0 ? (
                              <p className="text-[11px] text-[#9CA3AF] text-center py-6 italic">
                                {t("kanban.empty")}
                              </p>
                            ) : col._filtered.map((ld, idx) => (
                              <Draggable
                                key={ld.id || `${col.status}-${idx}`}
                                draggableId={ld.id || `${col.status}-${idx}`}
                                index={idx}
                              >
                                {(dragProvided, dragSnapshot) => (
                                  <KanbanCard
                                    ld={ld}
                                    isWon={col.status === "WON"}
                                    columnEnteredAt={ld.crm_status_updated_at || ld.saved_at}
                                    dragProvided={dragProvided}
                                    isDragging={dragSnapshot.isDragging}
                                    onOpenScore={() => setScoreLead(ld)}
                                    onOpenWhatsApp={() => setWaLead(ld)}
                                    onOpenDetails={() => setDrawerLead(ld)}
                                    onLookalike={() => openLookalikeFromLead(ld)}
                                    onSequence={() => setSequenceLead(ld)}
                                    onDelete={() => deleteLead(ld)}
                                    adBadge={adBadges[ld.name]}
                                    onRefreshAdBadge={() => refreshAdBadge(ld.name, ld)}
                                    t={t}
                                  />
                                )}
                              </Draggable>
                            ))}
                            {dropProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </section>
                  );
                })}
              </div>
            </div>
          </DragDropContext>
        </main>
      </div>

      <LookalikeModal
        open={lookalikeOpen}
        onClose={() => { setLookalikeOpen(false); load(); }}
        seedIds={lookalikeSeeds}
      />
      <ScoreBreakdownModal
        open={!!scoreLead}
        onClose={() => setScoreLead(null)}
        lead={scoreLead ? { ...scoreLead, _persisted: true } : null}
      />
      <WhatsAppModal
        open={!!waLead}
        onClose={() => setWaLead(null)}
        lead={waLead}
      />
      <WABulkModal
        open={waBulkOpen}
        onClose={() => setWaBulkOpen(false)}
        leads={filteredLeads}
        isPt={isPt}
      />
      <LeadDetailDrawer
        open={!!drawerLead}
        lead={drawerLead}
        onClose={() => setDrawerLead(null)}
        onChanged={load}
        onWhatsApp={(ld) => setWaLead(ld)}
      />
      <SequenceModal
        open={!!sequenceLead}
        onClose={() => setSequenceLead(null)}
        lead={sequenceLead}
      />
      <OutreachConfigModal
        open={outreachConfigOpen}
        onClose={() => setOutreachConfigOpen(false)}
      />
    </div>
  );
}

function SourceBadges({ ld }) {
  // Small inline icons next to the lead name — shows which social network the
  // lead has a connected profile on. Links open in new tab.
  const items = [
    ld.instagram_url && { href: ld.instagram_url, Icon: Instagram, color: "#E1306C", label: "Instagram" },
    ld.linkedin_url  && { href: ld.linkedin_url,  Icon: Linkedin,  color: "#0A66C2", label: "LinkedIn" },
    ld.youtube_url   && { href: ld.youtube_url,   Icon: Youtube,   color: "#FF0000", label: "YouTube" },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex items-center gap-0.5 shrink-0" data-testid="kanban-card-sources">
      {items.map(({ href, Icon, color, label }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-stop-card-click="true"
          onClick={(e) => e.stopPropagation()}
          title={label}
          className="inline-flex items-center justify-center w-4 h-4 rounded hover:scale-110 transition-transform"
          style={{ color }}
        >
          <Icon className="w-3 h-3" />
        </a>
      ))}
    </div>
  );
}

function MetaAdsBadge({ ld, badge, onRefresh, t }) {
  // badge: null (unknown) | {has_active_ads, active_count, loading, error}
  const isLoading = badge?.loading;
  const hasActive = !!badge?.has_active_ads;
  const hasData = badge && typeof badge.has_active_ads === "boolean";

  const handleClick = (e) => {
    e.stopPropagation();
    if (isLoading) return;
    onRefresh?.();
  };

  if (!hasData) {
    return (
      <button
        type="button"
        data-stop-card-click="true"
        data-testid={`kanban-meta-check-${ld.id}`}
        onClick={handleClick}
        title={t("kanban.meta_check_tooltip")}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB] transition-colors"
      >
        {isLoading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Megaphone className="w-2.5 h-2.5" />}
        {isLoading ? t("kanban.meta_checking") : t("kanban.meta_check_label")}
      </button>
    );
  }

  const cls = hasActive
    ? "bg-[#DCFCE7] text-[#15803D] hover:bg-[#BBF7D0]"
    : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]";
  const label = hasActive
    ? t("kanban.meta_active", { count: badge.active_count || 0 })
    : t("kanban.meta_inactive");

  return (
    <button
      type="button"
      data-stop-card-click="true"
      data-testid={`kanban-meta-${ld.id}`}
      onClick={handleClick}
      title={hasActive ? t("kanban.meta_active_tooltip", { count: badge.active_count || 0 }) : t("kanban.meta_inactive_tooltip")}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${cls}`}
    >
      {isLoading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Megaphone className="w-2.5 h-2.5" />}
      {label}
    </button>
  );
}

function KanbanCard({
  ld, isWon, columnEnteredAt, dragProvided, isDragging,
  onOpenScore, onOpenWhatsApp, onOpenDetails, onLookalike, onSequence, onDelete,
  adBadge, onRefreshAdBadge, t,
}) {
  const temp = TEMP_COLOR[ld.badge] || TEMP_COLOR.MORNO;
  const stale = daysSince(columnEnteredAt) >= STALE_DAYS;
  const staleD = daysSince(columnEnteredAt);
  const fu = ld.next_followup_at;
  const fuOverdue = fu ? new Date(fu).getTime() < Date.now() : false;
  const emailDaysAgo = ld.last_email_at ? daysSince(ld.last_email_at) : null;
  const ghost = ld.last_email_at == null && ld.crm_status !== "NEW";

  const phoneRaw = ld.phone_international || ld.phone || "";
  const telHref = phoneRaw ? `tel:${phoneRaw.replace(/[^+\d]/g, "")}` : null;
  const mailHref = ld.email ? `mailto:${ld.email}` : null;

  // Don't trigger drawer when clicking interactive children
  const handleCardClick = (e) => {
    if (e.target.closest("[data-stop-card-click=true]")) return;
    onOpenDetails();
  };

  return (
    <div
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      {...dragProvided.dragHandleProps}
      data-testid={`kanban-card-${ld.id}`}
      onClick={handleCardClick}
      className={`group relative border rounded-lg p-2.5 bg-white cursor-pointer transition-shadow ${isDragging ? "shadow-lg ring-2 ring-[#3B82F6]/40" : "hover:shadow-md"} ${stale ? "border-[#FCA5A5]" : "border-[#E5E7EB]"}`}
    >
      {/* Stale warning */}
      {stale && (
        <span
          data-testid={`kanban-card-stale-${ld.id}`}
          title={t("kanban.stale_tooltip", { days: staleD })}
          className="absolute -top-1.5 -left-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#DC2626] text-white shadow"
        >
          <Clock className="w-3 h-3" />
        </span>
      )}

      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <div className="font-semibold text-[12.5px] text-[#111827] truncate min-w-0">{ld.name}</div>
          <SourceBadges ld={ld} />
        </div>
        <span
          className="inline-flex items-center gap-0.5 text-[9.5px] font-bold px-1 py-0.5 rounded shrink-0"
          style={{ background: temp.bg, color: temp.text }}
        >
          <Thermometer className="w-2.5 h-2.5" />
          {ld.badge || "MORNO"}
        </span>
        {onDelete && (
          <button
            type="button"
            data-stop-card-click="true"
            data-testid={`kanban-delete-${ld.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title={t("kanban.delete_tooltip")}
            className="inline-flex items-center justify-center w-5 h-5 rounded text-[#9CA3AF] hover:text-[#DC2626] hover:bg-[#FEE2E2] transition-colors shrink-0"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      {ld.sector && (
        <div className="text-[11px] text-[#6B7280] truncate">{ld.sector}</div>
      )}
      {ld.address && (
        <div className="text-[10.5px] text-[#9CA3AF] truncate">{ld.address}</div>
      )}

      <div className="flex items-center justify-between text-[10.5px] text-[#6B7280] mt-1.5 gap-2">
        {phoneRaw ? (
          <span className="truncate">{phoneRaw}</span>
        ) : <span />}
        <button
          type="button"
          data-stop-card-click="true"
          data-testid={`kanban-score-${ld.id}`}
          onClick={(e) => { e.stopPropagation(); onOpenScore(); }}
          title={t("kanban.score_tooltip", { score: ld.digital_score ?? 0 })}
          className="inline-flex items-center gap-1 font-bold hover:text-[#3B82F6] cursor-pointer shrink-0"
          style={{ color: temp.text }}
        >
          <Thermometer className="w-3 h-3" />
          {ld.digital_score ?? "—"}
        </button>
      </div>

      {fu && (
        <div
          data-testid={`kanban-card-followup-${ld.id}`}
          title={fuOverdue ? t("kanban.followup_overdue", { date: fmtShortDate(fu) }) : t("kanban.followup_tooltip", { date: fmtShortDate(fu) })}
          className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${fuOverdue ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#EFF6FF] text-[#3B82F6]"}`}
        >
          {fuOverdue ? <AlertTriangle className="w-2.5 h-2.5" /> : <Calendar className="w-2.5 h-2.5" />}
          {fmtShortDate(fu)}
        </div>
      )}

      {ld.dedup_hits > 0 && (
        <span
          className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-[#7C3AED]"
          title={t("kanban.dedup_tooltip")}
        >
          <Sparkles className="w-2.5 h-2.5" /> {ld.dedup_hits}× {t("kanban.dedup_label")}
        </span>
      )}

      {/* Status-age + last-touch pills (Iter43) */}
      <div className="flex items-center gap-1.5 mt-2 text-[9.5px] font-semibold flex-wrap" data-testid={`kanban-touch-${ld.id}`}>
        <span
          title={t("kanban.status_age_tooltip", { days: staleD })}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${stale ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#F3F4F6] text-[#6B7280]"}`}
        >
          <Clock className="w-2.5 h-2.5" />
          {staleD === 0 ? t("kanban.today") : t("kanban.d_ago", { days: staleD })}
        </span>
        {emailDaysAgo !== null ? (
          <span
            data-testid={`kanban-card-email-${ld.id}`}
            title={t("kanban.last_email_tooltip", { days: emailDaysAgo, count: ld.touch_count || 0 })}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${emailDaysAgo > 7 ? "bg-[#FEF3C7] text-[#B45309]" : "bg-[#DCFCE7] text-[#15803D]"}`}
          >
            <Mail className="w-2.5 h-2.5" />
            {emailDaysAgo === 0 ? t("kanban.today") : t("kanban.d_ago", { days: emailDaysAgo })}
          </span>
        ) : ghost ? (
          <span
            data-testid={`kanban-card-silent-${ld.id}`}
            title={t("kanban.silent_tooltip")}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#DC2626]"
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            {t("kanban.silent_label")}
          </span>
        ) : null}
        {/* Meta Ads competitor badge — cache-only on mount, on-demand refresh (Iter45) */}
        <MetaAdsBadge ld={ld} badge={adBadge} onRefresh={onRefreshAdBadge} t={t} />
      </div>

      {/* Quick actions row */}
      <div
        data-stop-card-click="true"
        className="flex items-center gap-1 mt-2 opacity-90"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid={`kanban-wa-${ld.id}`}
          disabled={!phoneRaw}
          onClick={onOpenWhatsApp}
          title={phoneRaw ? t("ldd.action_wa") : t("ldd.no_phone")}
          className="inline-flex items-center justify-center w-7 h-7 rounded border border-[#E5E7EB] hover:border-[#25D366] hover:text-[#25D366] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <MessageCircle className="w-3 h-3" />
        </button>
        <a
          href={telHref || "#"}
          data-testid={`kanban-call-${ld.id}`}
          onClick={(e) => { if (!telHref) e.preventDefault(); }}
          aria-disabled={!telHref}
          title={telHref ? phoneRaw : t("ldd.no_phone")}
          className={`inline-flex items-center justify-center w-7 h-7 rounded border ${telHref ? "border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]" : "border-[#E5E7EB] opacity-30 cursor-not-allowed"} transition-colors`}
        >
          <Phone className="w-3 h-3" />
        </a>
        <a
          href={mailHref || "#"}
          data-testid={`kanban-email-${ld.id}`}
          onClick={(e) => { if (!mailHref) e.preventDefault(); }}
          aria-disabled={!mailHref}
          title={mailHref ? ld.email : t("ldd.no_email")}
          className={`inline-flex items-center justify-center w-7 h-7 rounded border ${mailHref ? "border-[#E5E7EB] hover:border-[#A855F7] hover:text-[#A855F7]" : "border-[#E5E7EB] opacity-30 cursor-not-allowed"} transition-colors`}
        >
          <Mail className="w-3 h-3" />
        </a>
        {isWon && (
          <button
            type="button"
            data-testid={`kanban-lookalike-${ld.id}`}
            onClick={onLookalike}
            data-stop-card-click="true"
            title={t("kanban.lookalike_seed_won")}
            className="ml-auto inline-flex items-center gap-1 h-7 px-2 rounded border border-[#A855F7] text-[#A855F7] text-[10px] font-semibold hover:bg-[#F3E8FF] transition-colors"
          >
            <Zap className="w-3 h-3" /> AI
          </button>
        )}
        {onSequence && (
          <button
            type="button"
            data-testid={`sequence-btn-${ld.id}`}
            onClick={(e) => { e.stopPropagation(); onSequence(); }}
            data-stop-card-click="true"
            title={t("lead.sequence_tooltip") || "Iniciar sequência de cold-email"}
            className={`${isWon ? "" : "ml-auto"} inline-flex items-center gap-1 h-7 px-2 rounded text-[10px] font-bold bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity`}
          >
            <Send className="w-3 h-3" /> SEQ
          </button>
        )}
      </div>
    </div>
  );
}

// Iter334 · Sector list rendered inside both the desktop popover and
// the mobile bottom sheet — shared to keep the two paths identical.
function SectorMenuList({ sectors, sectorFilter, toggleSector,
                            sectorCountByName, clearSectors, isPt,
                            mobile = false }) {
  return (
    <>
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--al-border)] mb-1">
        <button type="button" onClick={clearSectors}
          data-testid="kanban-sector-clear-menu"
          disabled={sectorFilter.size === 0}
          className="text-[12px] font-semibold text-[#C9A24E] hover:underline disabled:opacity-40">
          {isPt ? "Limpar" : "Clear"}
        </button>
        <span className="text-[10.5px] text-[var(--al-text-muted)]">
          {sectorFilter.size}/{sectors.length}
        </span>
      </div>
      {sectors.length === 0 ? (
        <div className="px-2 py-3 text-[11px] text-[var(--al-text-muted)] italic">
          {isPt ? "Nenhum setor" : "No sectors"}
        </div>
      ) : sectors.map((s) => {
        const n = sectorCountByName.get(s);
        const checked = sectorFilter.has(s);
        return (
          <label key={s}
            data-testid={`kanban-sector-option-${s}`}
            className={`flex items-center gap-2 px-2 ${mobile ? "py-3 min-h-[44px] text-[14px]" : "py-1.5 text-[12px]"} rounded-md cursor-pointer hover:bg-[var(--al-bg)] ${checked ? "bg-[#C9A24E]/10" : ""}`}>
            <input type="checkbox" checked={checked}
              onChange={() => toggleSector(s)}
              className={`accent-[#C9A24E] ${mobile ? "w-4 h-4" : ""}`} />
            <span className="flex-1 truncate">{s}</span>
            {n != null && (
              <span className="text-[10.5px] text-[var(--al-text-muted)]">{n}</span>
            )}
          </label>
        );
      })}
    </>
  );
}
